import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { FiArrowRight, FiExternalLink } from 'react-icons/fi';
import { Footer, MarketingHeader, SEO } from '@/components';
import { API_BASE_URL } from '@/lib/api';
import { track } from '@/lib/analytics';
import { requireDivorceProduct } from '@/lib/divorce-gate';
import {
  buildChecklist,
  formatFeeRange,
  getDivorceJurisdictionBySlug,
  isDivorceStateSlug,
  type ApprovedForm,
} from '@/lib/divorce';

export const Route = createFileRoute('/divorce/$state')({
  beforeLoad: async ({ params }) => {
    await requireDivorceProduct();
    if (!isDivorceStateSlug(params.state)) {
      throw redirect({ to: '/divorce' });
    }
  },
  component: DivorceStatePage,
});

function DivorceStatePage() {
  const { state } = Route.useParams();
  const jurisdiction = getDivorceJurisdictionBySlug(state);
  const [approvedForms, setApprovedForms] = useState<ApprovedForm[]>([]);
  const [tier, setTier] = useState<string>('navigator');

  useEffect(() => {
    if (!jurisdiction) return;
    track('Divorce State Page Viewed', { state: jurisdiction.code });
  }, [jurisdiction]);

  // Runtime enhancement only: approved-forms links come from the live registry.
  // The page renders fully from the baked registry without this call (prerender-safe).
  useEffect(() => {
    if (!jurisdiction) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(
          `${API_BASE_URL}/v1/public/divorce/${jurisdiction.code}`,
        );
        if (!resp.ok) return;
        const body = await resp.json();
        if (!cancelled && Array.isArray(body.approved_forms)) {
          setApprovedForms(body.approved_forms);
        }
        if (!cancelled && typeof body.tier === 'string') {
          setTier(body.tier);
        }
      } catch {
        // Static checklist is the fallback; no error UI needed.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jurisdiction]);

  if (!jurisdiction) return null;

  const path = `/divorce/${state}`;
  const checklist = buildChecklist(jurisdiction);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://harborcoparent.com/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Divorce',
        item: 'https://harborcoparent.com/divorce',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `Divorce in ${jurisdiction.name}`,
        item: `https://harborcoparent.com${path}`,
      },
    ],
  };

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title={`How to File for Divorce in ${jurisdiction.name} (Uncontested) | Harbor`}
        description={`Uncontested divorce in ${jurisdiction.name}: residency rules, official court forms, filing fees (${formatFeeRange(jurisdiction)}), fee waivers, and a step-by-step checklist. Educational only — not legal advice.`}
        path={path}
        jsonLd={breadcrumbJsonLd}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-10'>
        <div className='mx-auto max-w-4xl px-6'>
          <h1 className='mb-3 text-4xl font-bold text-gray-900 md:text-5xl'>
            Divorce in {jurisdiction.name}, step by step
          </h1>
          <p className='text-lg leading-relaxed text-gray-600 md:text-xl'>
            A plain-English checklist for an uncontested divorce in {jurisdiction.name},
            with links to the official court forms and fee-waiver information.
          </p>
          <p className='mt-3 text-sm text-gray-500'>
            Educational only and not legal advice. Court rules change — always confirm
            with your court&apos;s official site or clerk.
          </p>
        </div>
      </section>

      <section className='mx-auto max-w-4xl px-6 pt-8'>
        <div className='rounded-2xl border border-primary/30 bg-primary/5 p-6'>
          {tier === 'form_fill' ? (
            <>
              <h2 className='text-xl font-bold text-gray-900'>
                Want the paperwork done for you?
              </h2>
              <p className='mt-1 text-gray-600'>
                Answer plain-language questions and Harbor prepares your official{' '}
                {jurisdiction.name} forms. Your answers save automatically — start now,
                finish whenever.
              </p>
            </>
          ) : (
            <>
              <h2 className='text-xl font-bold text-gray-900'>
                Packet prep for {jurisdiction.name} is not open yet
              </h2>
              <p className='mt-1 text-gray-600'>
                Use the free checklist below, or start the interview to save your answers.
                Harbor will prepare official forms here once this state’s packet is reviewed.
              </p>
            </>
          )}
          <Link
            to='/divorce/interview'
            search={{ claimToken: undefined, state: jurisdiction.code, planToken: undefined }}
            onClick={() =>
              track('Divorce Interview CTA Clicked', {
                state: jurisdiction.code,
                packetReady: tier === 'form_fill',
              })
            }
            className='mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90'
          >
            Start the guided interview
            <FiArrowRight />
          </Link>
        </div>
      </section>

      <section className='mx-auto max-w-4xl px-6 py-12'>
        <ol className='space-y-6'>
          {checklist.map((step, idx) => (
            <li key={step.id} className='flex gap-4'>
              <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white'>
                {idx + 1}
              </div>
              <div>
                <h2 className='mb-1 text-lg font-semibold text-gray-900'>{step.title}</h2>
                <p className='leading-relaxed text-gray-600'>{step.body}</p>
                {step.linkUrl ? (
                  <a
                    href={step.linkUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() =>
                      track('Divorce Checklist Link Clicked', {
                        state: jurisdiction.code,
                        step: step.id,
                      })
                    }
                    className='mt-1 inline-flex items-center gap-1 font-medium text-primary hover:underline'
                  >
                    {step.linkLabel ?? step.linkUrl}
                    <FiExternalLink className='h-4 w-4' />
                  </a>
                ) : null}
              </div>
            </li>
          ))}
        </ol>

        {jurisdiction.notes ? (
          <div className='mt-10 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900'>
            <span className='font-semibold'>{jurisdiction.name} notes: </span>
            {jurisdiction.notes}
          </div>
        ) : null}

        {approvedForms.length > 0 ? (
          <div className='mt-10'>
            <h2 className='mb-3 text-2xl font-bold text-gray-900'>
              Commonly used {jurisdiction.name} forms
            </h2>
            <ul className='space-y-2'>
              {approvedForms.map((form) => (
                <li key={form.source_url}>
                  <a
                    href={form.source_url}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() =>
                      track('Divorce Official Form Link Clicked', {
                        state: jurisdiction.code,
                        form: form.form_number ?? form.title,
                      })
                    }
                    className='inline-flex items-center gap-1 text-primary hover:underline'
                  >
                    {form.form_number ? `${form.form_number} — ` : ''}
                    {form.title}
                    {form.revision_label ? ` (${form.revision_label})` : ''}
                    <FiExternalLink className='h-4 w-4' />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <section className='bg-gray-50 py-12'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h2 className='mb-3 text-2xl font-bold text-gray-900'>
            Divorcing with kids? Your parenting plan comes first.
          </h2>
          <p className='mx-auto mb-6 max-w-2xl text-gray-600'>
            {jurisdiction.name} courts expect a parenting plan in any divorce with minor
            children. Build one free with Harbor, then keep running it in the app after
            the divorce — calendar, expenses, and records in one place.
          </p>
          <div className='flex flex-wrap items-center justify-center gap-4'>
            <Link
              to='/parenting-plan'
              onClick={() =>
                track('Divorce State Parenting Plan CTA Clicked', {
                  state: jurisdiction.code,
                })
              }
              className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90'
            >
              Build your parenting plan
              <FiArrowRight />
            </Link>
            <Link
              to='/child-support-calculator/$state'
              params={{ state }}
              onClick={() =>
                track('Divorce State Child Support CTA Clicked', {
                  state: jurisdiction.code,
                })
              }
              className='inline-flex items-center gap-2 rounded-full border border-primary px-6 py-3 font-semibold text-primary transition-colors hover:bg-primary/5'
            >
              Estimate child support
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
